/**
 * Column Name Mapping Utilities
 * 
 * Handles mapping raw CSV column names to clean display names for the UI
 * while preserving original names for unit extraction and other processing.
 */

export interface ColumnMapping {
  originalName: string;
  displayName: string;
  unit?: string; // Optional unit override
}

export interface ColumnMappingConfig {
  // Explicit column name mappings
  columnMappings?: Record<string, string>;
  // Whether to apply automatic cleaning (remove parenthetical units)
  autoClean?: boolean;
  // Custom regex patterns for cleaning
  cleaningPatterns?: Array<{ pattern: RegExp; replacement: string }>;
}

/**
 * Utility class for mapping CSV column names to clean display names
 */
export class ColumnNameMapper {
  
  /**
   * Automatically clean a column name by removing common patterns
   * - Removes parenthetical units: "Temperature (°C)" → "Temperature"
   * - Trims whitespace
   * - Handles edge cases
   */
  static cleanColumnName(columnName: string): string {
    if (!columnName || typeof columnName !== 'string') {
      return '';
    }

    let cleaned = columnName.trim();
    
    // Remove parenthetical content (units, descriptions, etc.)
    // Matches: (units), (°C), (degrees), (cps), etc.
    cleaned = cleaned.replace(/\s*\([^)]*\)\s*$/g, '');
    
    // Remove trailing whitespace after cleaning
    cleaned = cleaned.trim();
    
    return cleaned;
  }

  /**
   * Apply explicit mappings with fallback to automatic cleaning
   */
  static mapColumnName(
    columnName: string, 
    mappings?: Record<string, string>, 
    autoClean: boolean = true
  ): string {
    if (!columnName || typeof columnName !== 'string') {
      return columnName || '';
    }

    // First, check for explicit mapping
    if (mappings && mappings[columnName]) {
      return mappings[columnName];
    }

    // Fallback to automatic cleaning if enabled
    if (autoClean) {
      return this.cleanColumnName(columnName);
    }

    // No mapping, return original
    return columnName;
  }

  /**
   * Get complete column mapping information
   * Returns both display name and preserves original for unit extraction
   */
  static getColumnMapping(
    columnName: string, 
    config?: ColumnMappingConfig
  ): ColumnMapping {
    const autoClean = config?.autoClean !== false; // Default to true
    const mappings = config?.columnMappings;
    
    const displayName = this.mapColumnName(columnName, mappings, autoClean);
    
    return {
      originalName: columnName,
      displayName,
      unit: config?.columnMappings?.[columnName + '_unit'] // Allow unit overrides
    };
  }

  /**
   * Apply custom cleaning patterns
   */
  static applyCustomPatterns(
    columnName: string, 
    patterns: Array<{ pattern: RegExp; replacement: string }>
  ): string {
    let result = columnName;
    
    for (const { pattern, replacement } of patterns) {
      result = result.replace(pattern, replacement);
    }
    
    return result.trim();
  }

  /**
   * Batch process multiple column names
   */
  static mapColumnNames(
    columnNames: string[], 
    config?: ColumnMappingConfig
  ): ColumnMapping[] {
    return columnNames.map(name => this.getColumnMapping(name, config));
  }

  /**
   * Extract unit from original column name using common patterns
   * This preserves the existing unit extraction logic while working with cleaned names
   */
  static extractUnitFromColumnName(columnName: string): string | null {
    if (!columnName || typeof columnName !== 'string') {
      return null;
    }

    // Look for parenthetical units
    const unitMatch = columnName.match(/\(([^)]+)\)$/);
    if (unitMatch) {
      const unit = unitMatch[1];
      
      // Normalize common unit patterns
      if (unit.toLowerCase().includes('degrees') || unit.toLowerCase() === 'deg') {
        return 'degrees';
      }
      if (unit.toLowerCase().includes('celsius') || unit === '°C') {
        return '°C';
      }
      if (unit.toLowerCase() === 'cps') {
        return 'cps';
      }
      if (unit.toLowerCase() === 'ms') {
        return 'ms';
      }
      if (unit.toLowerCase().includes('gauss')) {
        return 'gauss';
      }
      if (unit.toLowerCase() === 'g') {
        return 'g';
      }
      if (unit.toLowerCase() === 'mv') {
        return 'mV';
      }
      if (unit.toLowerCase() === 'ma') {
        return 'mA';
      }
      if (unit.toLowerCase() === 'v') {
        return 'V';
      }
      
      return unit; // Return as-is if not recognized
    }

    return null;
  }

  /**
   * Validate that a mapping configuration is valid
   */
  static validateConfig(config: ColumnMappingConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (config.columnMappings) {
      for (const [original, mapped] of Object.entries(config.columnMappings)) {
        if (!original || !mapped) {
          errors.push(`Invalid mapping: "${original}" → "${mapped}"`);
        }
        if (typeof original !== 'string' || typeof mapped !== 'string') {
          errors.push(`Mapping values must be strings: "${original}" → "${mapped}"`);
        }
      }
    }

    if (config.cleaningPatterns) {
      for (const pattern of config.cleaningPatterns) {
        if (!(pattern.pattern instanceof RegExp)) {
          errors.push(`Cleaning pattern must be a RegExp: ${pattern.pattern}`);
        }
        if (typeof pattern.replacement !== 'string') {
          errors.push(`Replacement must be a string: ${pattern.replacement}`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

/**
 * Common column mapping configurations for reuse
 */
export const COMMON_COLUMN_MAPPINGS = {
  // Temperature variations
  'Tool Temperature (°C)': 'Tool Temperature',
  'Temperature (Celsius)': 'Temperature',
  'ADC Temperature (mV)': 'ADC Temperature',
  
  // Directional measurements
  'Gravity Tool-Face (degrees)': 'Gravity Toolface',
  'Gravity Toolface (degrees)': 'Gravity Toolface', 
  'Magnetic Tool-Face (degrees)': 'Magnetic Toolface',
  'Magnetic Toolface (degrees)': 'Magnetic Toolface',
  'Inclination (Degrees)': 'Inclination',
  'Inclination (degrees)': 'Inclination',
  'Azimuth (Degrees)': 'Azimuth',
  'Azimuth (degrees)': 'Azimuth',
  
  // Gamma measurements
  'Rotational Gamma CPS (cps)': 'Rotational Gamma CPS',
  'Gamma1 (API/CPS)': 'Gamma1',
  'Gamma2 (API/CPS)': 'Gamma2',
  'Gamma3 (API/CPS)': 'Gamma3',
  
  // Time measurements
  'Clock Ticks (ms)': 'Clock Ticks',
  
  // Voltage measurements
  'Battery (V)': 'Battery',
  'System Current (V)': 'System Current',
  
  // Magnetometer measurements - standardize to Mx, My, Mz
  'Mx (gauss)': 'Mx',
  'My (gauss)': 'My',
  'Mz (gauss)': 'Mz',
  'Bx': 'Mx',
  'By': 'My', 
  'Bz': 'Mz',
  'Hx': 'Mx',
  'Hy': 'My',
  'Hz': 'Mz',
  
  // Accelerometer/Gravity measurements - standardize to Gx, Gy, Gz
  'Ax (g)': 'Gx',
  'Ay (g)': 'Gy',
  'Az (g)': 'Gz',
  'Ax': 'Gx',
  'Ay': 'Gy',
  'Az': 'Gz',
  'Gx': 'Gx',
  'Gy': 'Gy',
  'Gz': 'Gz',
  'Ax2 (g)': 'Gx2',
  'Ay2 (g)': 'Gy2',
  'Az2 (g)': 'Gz2',
  'Gravity (g)': 'Total Gravity',
  'Total Grav': 'Total Gravity'
}; 